/**
 * หน้าพนักงาน — เส้นทาง: กล้อง → ยืนยันก่อนบันทึก → ส่ง (packages/slip-intake ผ่าน dev server) → สำเร็จ / สลิปซ้ำ / ส่งไม่ได้ (ลองใหม่)
 * ตรรกะหน้าจออยู่ที่ screens.ts (บริสุทธิ์ · มีเทสต์) — ไฟล์นี้ผูก DOM อย่างเดียว
 */
import "./app.css";
import type { IntakeResult } from "@pidyod/slip-intake";
import { fetchMe, submitSlip } from "./api.js";
import { mountCamera, renderCamera, type CameraHandle, type CameraPhase, type CaptureEvent } from "./components/camera.js";
import type { KindId } from "./components/slip.js";
import { nextScreen, renderScreen, shopOf, type AppState, type Captured } from "./screens.js";

/* ------------------------------------------------------------------ DOM ------------------------------------------------------------------ */

const app = document.getElementById("app")!;
const state: AppState = { me: null, shopIndex: 0, screen: { name: "loading" }, fatal: null };
let camera: CameraHandle | null = null;

function paint(): void {
  camera?.stop(); camera = null;
  app.innerHTML = renderScreen(state);
  if (state.screen.name === "camera" && state.me) {
    camera = mountCamera(app, { shopName: shopOf(state).shop_name, staffName: state.me.staff_name }, {
      capture: onCapture,
      switchShop: () => { if (state.me && state.me.shops.length > 1) { state.shopIndex = (state.shopIndex + 1) % state.me.shops.length; paint(); } },
    });
  }
}

function onCapture(e: CaptureEvent): void {
  const captured: Captured = {
    blob: e.image.blob, previewUrl: URL.createObjectURL(e.image.blob), sha256: e.image.sha256,
    qrBankCode: e.qr.bank_code, transRef: e.qr.trans_ref, qrOk: e.qr.qr_ok, capturedAt: new Date(), clientUuid: crypto.randomUUID(),
  };
  state.screen = { name: "confirm", captured, kind: "sale", sending: false, error: null };
  paint();
}

async function save(): Promise<void> {
  if (state.screen.name !== "confirm" || state.screen.sending) return;
  const { captured, kind } = state.screen;
  state.screen = { ...state.screen, sending: true, error: null }; paint();
  let result: IntakeResult;
  try {
    result = await submitSlip({ blob: captured.blob, shopId: shopOf(state).shop_id, clientUuid: captured.clientUuid, kind });
  } catch (e) {
    // เน็ตหลุด / เซิร์ฟเวอร์ล่ม — ภาพยังอยู่ ลองใหม่ด้วย client_uuid เดิม (ส่งซ้ำปลอดภัย)
    result = { outcome: "failed", error: e instanceof Error ? e.message : String(e), note: "ส่งไม่ถึงเซิร์ฟเวอร์ — ภาพยังอยู่ กดลองส่งใหม่ได้" };
  }
  state.screen = nextScreen(result, captured, kind, shopOf(state));
  paint();
}

app.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!btn) return;
  switch (btn.dataset["action"]) {
    case "save": void save(); break;
    case "kind": if (state.screen.name === "confirm") { state.screen = { ...state.screen, kind: btn.dataset["kind"] as KindId }; paint(); } break;
    case "retake": case "next":
      if ("captured" in state.screen) URL.revokeObjectURL(state.screen.captured.previewUrl);
      state.screen = { name: "camera" }; paint(); break;
    case "reload": location.reload(); break;
  }
});

const mock = new URLSearchParams(location.search).get("mock");
if (mock) {
  // เทียบกับ mockup ในเบราว์เซอร์ที่ไม่มีกล้อง — ไม่มีผลกับการใช้งานจริง
  const captured: Captured = { blob: new Blob(), previewUrl: "", sha256: "", qrBankCode: "004", transRef: "014210032XQ (ตัวอย่าง)", qrOk: true, capturedAt: new Date(), clientUuid: "" };
  state.me = { mode: "dev", liffId: null, staff_id: "", staff_name: "น้ำฝน", shops: [{ shop_id: "", shop_name: "ถลาง มินิมาร์ท" }] };
  if (mock === "confirm") state.screen = { name: "confirm", captured, kind: "sale", sending: false, error: null };
  else if (mock === "success") state.screen = { name: "success", captured, recordedAt: new Date() };
  else if (mock === "duplicate") state.screen = { name: "duplicate", captured, firstSeen: { at: new Date(), shopName: "ถลาง มินิมาร์ท" } };
  if (mock === "confirm" || mock === "success" || mock === "duplicate") app.innerHTML = renderScreen(state);
  else app.innerHTML = renderCamera({ shopName: "ถลาง มินิมาร์ท", staffName: "น้ำฝน", autoCapture: true, torch: false, torchAvailable: false, phase: mock as CameraPhase });
} else {
  fetchMe().then((me) => { state.me = me; state.screen = { name: "camera" }; paint(); })
    .catch(() => {
      // กฎเหล็ก #1: ห้ามขวางพนักงานไม่ให้บันทึกสลิป แม้ /api/me ใช้งานไม่ได้ (Worker ยังไม่มี endpoint จริง / เน็ตหลุด) — เปิดกล้องต่อแบบไม่ทราบตัวตน
      state.me = { mode: "dev", liffId: null, staff_id: "", staff_name: "พนักงาน (ออฟไลน์)", shops: [{ shop_id: "", shop_name: "ไม่ทราบร้าน — เชื่อมระบบไม่ได้" }] };
      state.screen = { name: "camera" };
      paint();
    });
}
